import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { PixelAvatar, UserAvatar } from '@/components/PixelAvatar';
import { toast } from 'sonner';
import { Trophy, Flame, BookOpen, CheckSquare, Timer, ArrowLeft, Users, Globe, Crown, Zap } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import PhraseStatsSection from '@/components/PhraseStatsSection';

const API_URL = getApiUrl();

const ACTIVITY_TYPES = [
  { id: 'overall', label: 'Overall', labelEs: 'General', icon: Trophy, color: 'text-yellow-500' },
  { id: 'xp', label: 'XP', labelEs: 'XP', icon: Zap, color: 'text-yellow-500' },
  { id: 'phrase', label: 'Phrases', labelEs: 'Frases', icon: BookOpen, color: 'text-blue-500' },
  { id: 'todo', label: 'To-Do', labelEs: 'Tareas', icon: CheckSquare, color: 'text-green-500' },
];

const TIME_PERIODS = [
  { id: 'weekly', label: 'This Week', labelEs: 'Esta Semana' },
  { id: 'monthly', label: 'This Month', labelEs: 'Este Mes' },
  { id: 'alltime', label: 'All Time', labelEs: 'Todo el Tiempo' },
];

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isSpanish } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('overall');
  const [friendsOnly, setFriendsOnly] = useState(false);
  const [timePeriod, setTimePeriod] = useState('alltime');
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use different endpoint for XP leaderboard
      const endpoint = activeTab === 'xp' 
        ? `/api/friends/leaderboard/xp?friends_only=${friendsOnly}&period=${timePeriod}`
        : `/api/friends/leaderboard?activity_type=${activeTab}&friends_only=${friendsOnly}&period=${timePeriod}`;
      
      const { data, error } = await apiFetch(endpoint);
      
      if (!error && data) {
        setLeaderboard(data.leaderboard || []);
        setMyRank(data.my_rank);
        setTotalParticipants(data.total_participants);
      } else {
        toast.error(isSpanish ? 'Error al cargar clasificación' : 'Failed to load leaderboard');
      }
    } catch (error) {
      console.error('Leaderboard error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, friendsOnly, timePeriod, isSpanish]);

  // Fetch my stats
  const fetchMyStats = useCallback(async () => {
    try {
      const { data } = await apiFetch('/api/friends/leaderboard/my-stats');
      if (data) {
        setMyStats(data);
      }
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeaderboard();
      fetchMyStats();
    }
  }, [isAuthenticated, fetchLeaderboard, fetchMyStats]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <Header />
        <div className="container py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  const currentActivity = ACTIVITY_TYPES.find(a => a.id === activeTab);
  const Icon = currentActivity?.icon || Trophy;

  return (
    <div className="min-h-screen bg-background pb-20">
      <Header />
      
      <div className="container py-8 max-w-3xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isSpanish ? 'Atrás' : 'Back'}
        </Button>
        
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="h-8 w-8 text-yellow-500" />
          <div>
            <h1 className="text-3xl font-bold">{isSpanish ? 'Estadísticas y Ranking' : 'Stats & Leaderboard'}</h1>
            <p className="text-muted-foreground">
              {isSpanish ? 'Compite con amigos y revisa tu progreso' : 'Compete with friends and review your progress'}
            </p>
          </div>
        </div>

        {/* Leaderboard above, stats below — flex with `order-` to swap
            without rewriting the JSX. Leaderboard info first as requested. */}
        <div className="flex flex-col gap-6 mt-6">

        {/* My Stats Card (order-2 — renders after leaderboard) */}
        {myStats && (
          <Card className="order-2 bg-gradient-to-r from-[#00E5CC]/10 to-purple-500/10" data-testid="my-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Flame className="h-5 w-5 text-orange-500" />
                {isSpanish ? 'Mis Rachas' : 'My Streaks'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {ACTIVITY_TYPES.map(activity => {
                  const ActivityIcon = activity.icon;
                  
                  // Special handling for XP
                  if (activity.id === 'xp') {
                    return (
                      <div 
                        key={activity.id} 
                        className={`p-3 rounded-lg bg-white/70 dark:bg-white/10 border border-transparent dark:border-white/10 ${activeTab === activity.id ? 'ring-2 ring-[#00E5CC]' : ''}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <ActivityIcon className={`h-4 w-4 ${activity.color}`} />
                          <span className="text-xs font-medium">{isSpanish ? activity.labelEs : activity.label}</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold">{(user?.total_xp || 0).toLocaleString()}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">Lvl {user?.level || 1}</span>
                      </div>
                    );
                  }
                  
                  const stats = myStats[activity.id] || { current: 0, longest: 0 };
                  return (
                    <div 
                      key={activity.id} 
                      className={`p-3 rounded-lg bg-white/70 dark:bg-white/10 border border-transparent dark:border-white/10 ${activeTab === activity.id ? 'ring-2 ring-[#00E5CC]' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <ActivityIcon className={`h-4 w-4 ${activity.color}`} />
                        <span className="text-xs font-medium">{isSpanish ? activity.labelEs : activity.label}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold">{stats.current}</span>
                        <span className="text-xs text-muted-foreground">/ {stats.longest} max</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!myStats.display_name && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-700/50 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {isSpanish 
                      ? '⚠️ Configura un nombre para mostrar en Configuración para aparecer en la clasificación general.'
                      : '⚠️ Set a display name in Settings to appear on the overall leaderboard.'}
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => navigate('/settings')}
                  >
                    {isSpanish ? 'Ir a Configuración' : 'Go to Settings'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Tabs (order-1 — renders FIRST per user request) */}
        <Card className="order-1" data-testid="leaderboard-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Icon className={`h-5 w-5 ${currentActivity?.color}`} />
                {isSpanish ? currentActivity?.labelEs : currentActivity?.label} {isSpanish ? 'Clasificación' : 'Leaderboard'}
              </CardTitle>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* Time Period Filter */}
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                  {TIME_PERIODS.map(period => (
                    <button
                      key={period.id}
                      onClick={() => setTimePeriod(period.id)}
                      className={`px-2 py-1 text-xs rounded-md transition-colors ${
                        timePeriod === period.id 
                          ? 'bg-[#00E5CC] text-[#0d1f2d] font-semibold' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {isSpanish ? period.labelEs : period.label}
                    </button>
                  ))}
                </div>
                
                {/* Friends/Overall Toggle */}
                <div className="flex items-center gap-2">
                  <Globe className={`h-4 w-4 ${!friendsOnly ? 'text-[#00E5CC]' : 'text-muted-foreground'}`} />
                  <Switch
                    id="friends-toggle"
                    checked={friendsOnly}
                    onCheckedChange={setFriendsOnly}
                  />
                  <Users className={`h-4 w-4 ${friendsOnly ? 'text-[#00E5CC]' : 'text-muted-foreground'}`} />
                  <Label htmlFor="friends-toggle" className="text-sm cursor-pointer">
                    {friendsOnly ? (isSpanish ? 'Amigos' : 'Friends') : (isSpanish ? 'General' : 'Overall')}
                  </Label>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Activity Type Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
              <TabsList className="grid grid-cols-4 w-full">
                {ACTIVITY_TYPES.map(activity => {
                  const ActivityIcon = activity.icon;
                  return (
                    <TabsTrigger 
                      key={activity.id} 
                      value={activity.id}
                      className="flex items-center gap-1"
                    >
                      <ActivityIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{isSpanish ? activity.labelEs : activity.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>

            {/* Leaderboard List */}
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  {friendsOnly 
                    ? (isSpanish ? 'Aún no hay amigos en la clasificación' : 'No friends on the leaderboard yet')
                    : (isSpanish ? 'Sé el primero en aparecer en la clasificación' : 'Be the first on the leaderboard')}
                </p>
                {!friendsOnly && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {isSpanish 
                      ? 'Configura un nombre para mostrar para aparecer aquí'
                      : 'Set a display name to appear here'}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.user_id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      entry.is_me 
                        ? 'bg-[#00E5CC]/10 border border-[#00E5CC]/30' 
                        : index % 2 === 0 ? 'bg-muted/30' : ''
                    }`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    {/* Rank */}
                    <div className="w-10 text-center font-bold text-lg">
                      {getRankEmoji(entry.rank)}
                    </div>
                    
                    {/* Avatar */}
                    <PixelAvatar avatarId={entry.avatar || 'dino'} colorId={entry.avatar_color || 'aqua'} size={36} showBorder={false} />
                    
                    {/* Name & Streak */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium truncate ${entry.is_me ? 'text-[#00E5CC]' : ''}`}>
                          {/* Full display_name — this is the user's
                              chosen username (which can be a multi-word
                              phrase like "Love Yourself"), NOT a real
                              first/last name we should split. */}
                          {entry.display_name}
                        </span>
                        {entry.is_me && (
                          <Badge variant="outline" className="text-xs">
                            {isSpanish ? 'Tú' : 'You'}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {timePeriod === 'alltime'
                          ? (isSpanish ? 'Racha actual' : 'Current streak') + ': ' + entry.current_streak + ' ' + (isSpanish ? 'días' : 'days')
                          : (isSpanish ? 'Máximo' : 'Best') + ': ' + entry.longest_streak + ' ' + (isSpanish ? 'días' : 'days')
                        }
                      </div>
                    </div>
                    
                    {/* Current Streak / Best Streak / XP — the big number
                        reflects the selected period. For "All Time" we
                        show the user's lifetime BEST streak (longest_streak),
                        not their current/active streak. */}
                    <div className="text-right">
                      {activeTab === 'xp' ? (
                        <>
                          <div className="flex items-center gap-1 text-xl font-bold">
                            <Zap className="h-5 w-5 text-yellow-500" />
                            {entry.total_xp?.toLocaleString() || 0}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Level {entry.level || 1}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-1 text-xl font-bold">
                            <Flame className="h-5 w-5 text-orange-500" />
                            {timePeriod === 'alltime' ? entry.longest_streak : entry.current_streak}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {timePeriod === 'alltime'
                              ? (isSpanish ? 'mejor' : 'best')
                              : (isSpanish ? 'racha' : 'streak')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Show my rank if not in top 50 */}
            {myRank && myRank > 50 && !leaderboard.find(e => e.is_me) && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground text-center">
                  {isSpanish ? 'Tu posición' : 'Your rank'}: <strong>#{myRank}</strong> {isSpanish ? 'de' : 'of'} {totalParticipants}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        {/* Site-wide phrase stats — merged in from the old /statistics page */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#00E5CC]" />
            {isSpanish ? 'Estadísticas de Frases' : 'Phrase Statistics'}
          </h2>
          <PhraseStatsSection />
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
